import React from "react";
import { Shield, ShieldCheck, ShieldAlert, ShieldX } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface InspectionBadgeProps {
  rating?: string | null;
  lastInspectionDate?: string | null;
  size?: "sm" | "md";
}

const ratingConfig: Record<string, { label: string; icon: typeof Shield; className: string; description: string }> = {
  compliant: {
    label: "Compliant",
    icon: ShieldCheck,
    className: "bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100",
    description: "Most standards met in latest HIQA inspection",
  },
  substantially_compliant: {
    label: "Substantially Compliant",
    icon: Shield,
    className: "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100",
    description: "Mixed results in latest HIQA inspection",
  },
  non_compliant: {
    label: "Non-Compliant",
    icon: ShieldAlert,
    className: "bg-red-100 text-red-800 border-red-200 hover:bg-red-100",
    description: "Significant issues found in latest HIQA inspection",
  },
  unknown: {
    label: "Not Rated",
    icon: ShieldX,
    className: "bg-muted text-muted-foreground border-border hover:bg-muted",
    description: "HIQA inspection data not available",
  },
};

export function InspectionBadge({ rating, lastInspectionDate, size = "md" }: InspectionBadgeProps) {
  if (!rating || rating === "error") return null;

  const config = ratingConfig[rating] || ratingConfig.unknown;
  const Icon = config.icon;
  const iconSize = size === "sm" ? 12 : 14;

  const formattedDate = lastInspectionDate
    ? new Date(lastInspectionDate).toLocaleDateString("en-IE", { month: "short", year: "numeric" })
    : null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`${config.className} ${size === "sm" ? "text-xs px-1.5 py-0" : "text-xs px-2 py-0.5"} font-medium gap-1 cursor-default`}>
            <Icon size={iconSize} />
            {size === "md" && <span>HIQA: {config.label}</span>}
            {size === "sm" && <span>{config.label}</span>}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-medium">{config.description}</p>
          {formattedDate && <p className="text-xs text-muted-foreground">Last inspected: {formattedDate}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
